import React, { useState, useEffect } from 'react';
import { useLanguage } from '../../context/LanguageContext';

/**
 * Country Form Component
 *
 * Provides form for creating/editing countries with:
 * - Country name and ISO code validation
 * - Flag emoji picker
 * - Active status toggle
 * - Sort order management
 * - Real-time validation feedback
 */
const CountryForm = ({ country, onSubmit, onCancel }) => {
  const { t } = useLanguage();
  const [formData, setFormData] = useState({
    name: '',
    code: '',
    flag_emoji: '🏳️',
    is_active: true,
    sort_order: 0
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);

  const commonFlags = [
    '🇩🇪', // Germany
    '🇨🇦', // Canada
    '🇦🇺', // Australia
    '🇬🇧', // United Kingdom
    '🇺🇸', // United States
    '🇳🇿', // New Zealand
    '🇸🇬', // Singapore
    '🇲🇾', // Malaysia
    '🇦🇪', // United Arab Emirates
    '🇫🇷', // France
    '🇸🇪', // Sweden
    '🇳🇱', // Netherlands
    '🇨🇭', // Switzerland
    '🇮🇹', // Italy
    '🇪🇸', // Spain
    '🇯🇵', // Japan
    '🇰🇷', // South Korea
    '🇮🇳', // India
    '🇨🇳', // China
    '🇧🇷', // Brazil
  ];

  useEffect(() => {
    if (country) {
      setFormData({
        name: country.name || '',
        code: country.code || '',
        flag_emoji: country.flag_emoji || '🏳️',
        is_active: country.is_active !== false,
        sort_order: country.sort_order || 0
      });
    }
  }, [country]);

  const validateForm = () => {
    const newErrors = {};

    // Name validation
    if (!formData.name.trim()) {
      newErrors.name = t('admin.countries.validation.name_required', 'Country name is required');
    } else if (formData.name.trim().length < 2) {
      newErrors.name = t('admin.countries.validation.name_min_length', 'Country name must be at least 2 characters');
    }

    // Code validation (ISO 3166-1 alpha-3 format)
    const codePattern = /^[A-Z]{3}$/;
    if (!formData.code.trim()) {
      newErrors.code = t('admin.countries.validation.code_required', 'Country code is required');
    } else if (!codePattern.test(formData.code.toUpperCase())) {
      newErrors.code = t('admin.countries.validation.code_format', 'Country code must be exactly 3 uppercase letters');
    }

    // Sort order validation
    if (isNaN(formData.sort_order) || formData.sort_order < 0) {
      newErrors.sort_order = t('admin.countries.validation.sort_order_invalid', 'Sort order must be a non-negative number');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;

    if (name === 'code') {
      setFormData(prev => ({
        ...prev,
        [name]: value.toUpperCase()
      }));
    } else if (type === 'checkbox') {
      setFormData(prev => ({
        ...prev,
        [name]: checked
      }));
    } else if (type === 'number') {
      setFormData(prev => ({
        ...prev,
        [name]: parseInt(value) || 0
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }

    // Clear error for this field
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    try {
      await onSubmit(formData);
    } catch (error) {
      console.error('Error submitting form:', error);
      setErrors({
        submit: t('admin.countries.validation.submit_error', 'Failed to save country. Please try again.')
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-medium text-white">
          {country
            ? t('admin.countries.edit_title', 'Edit Country')
            : t('admin.countries.create_title', 'Create New Country')
          }
        </h3>
        <button
          onClick={onCancel}
          className="text-gray-400 hover:text-gray-200 transition-colors duration-150"
        >
          <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
          </svg>
        </button>
      </div>

      <form onSubmit={handleSubmit} className="space-y-6">
        {/* Country Name */}
        <div>
          <label htmlFor="name" className="block text-sm font-medium text-gray-300 mb-2">
            {t('admin.countries.form.name', 'Country Name')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="name"
            name="name"
            value={formData.name}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-700 text-white ${errors.name ? 'border-red-500' : 'border-gray-600'
              }`}
            placeholder={t('admin.countries.form.name_placeholder', 'e.g., Germany')}
            disabled={loading}
          />
          {errors.name && (
            <p className="mt-1 text-sm text-red-400">{errors.name}</p>
          )}
        </div>

        {/* Country Code */}
        <div>
          <label htmlFor="code" className="block text-sm font-medium text-gray-300 mb-2">
            {t('admin.countries.form.code', 'ISO Code')} <span className="text-red-500">*</span>
          </label>
          <input
            type="text"
            id="code"
            name="code"
            value={formData.code}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-700 text-white ${errors.code ? 'border-red-500' : 'border-gray-600'
              }`}
            placeholder={t('admin.countries.form.code_placeholder', 'e.g., DEU')}
            maxLength={3}
            disabled={loading}
          />
          {errors.code && (
            <p className="mt-1 text-sm text-red-400">{errors.code}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            {t('admin.countries.form.code_help', 'ISO 3166-1 alpha-3 code (3 uppercase letters)')}
          </p>
        </div>

        {/* Flag Emoji */}
        <div>
          <label htmlFor="flag_emoji" className="block text-sm font-medium text-gray-300 mb-2">
            {t('admin.countries.form.flag', 'Flag Emoji')}
          </label>
          <div className="space-y-3">
            <input
              type="text"
              id="flag_emoji"
              name="flag_emoji"
              value={formData.flag_emoji}
              onChange={handleInputChange}
              className="w-full px-3 py-2 border border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-700 text-white"
              placeholder={t('admin.countries.form.flag_placeholder', 'e.g., 🇩🇪')}
              disabled={loading}
            />
            <div>
              <p className="text-xs text-gray-500 mb-2">
                {t('admin.countries.form.quick_select', 'Quick select:')}
              </p>
              <div className="grid grid-cols-10 gap-2">
                {commonFlags.map((flag) => (
                  <button
                    key={flag}
                    type="button"
                    onClick={() => setFormData(prev => ({ ...prev, flag_emoji: flag }))}
                    className={`p-2 text-lg border rounded hover:bg-gray-600 transition-colors duration-150 ${formData.flag_emoji === flag ? 'border-blue-500 bg-blue-900/30' : 'border-gray-600 bg-gray-700'
                      }`}
                    title={flag}
                  >
                    {flag}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>

        {/* Sort Order */}
        <div>
          <label htmlFor="sort_order" className="block text-sm font-medium text-gray-300 mb-2">
            {t('admin.countries.form.sort_order', 'Display Order')}
          </label>
          <input
            type="number"
            id="sort_order"
            name="sort_order"
            value={formData.sort_order}
            onChange={handleInputChange}
            className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent bg-gray-700 text-white ${errors.sort_order ? 'border-red-500' : 'border-gray-600'
              }`}
            placeholder="0"
            min="0"
            disabled={loading}
          />
          {errors.sort_order && (
            <p className="mt-1 text-sm text-red-400">{errors.sort_order}</p>
          )}
          <p className="mt-1 text-xs text-gray-500">
            {t('admin.countries.form.sort_order_help', 'Lower numbers appear first in the list')}
          </p>
        </div>

        {/* Active Status */}
        <div>
          <label className="flex items-center space-x-3 cursor-pointer">
            <input
              type="checkbox"
              name="is_active"
              checked={formData.is_active}
              onChange={handleInputChange}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-600 rounded bg-gray-700"
              disabled={loading}
            />
            <span className="text-sm font-medium text-gray-300">
              {t('admin.countries.form.active', 'Active')}
            </span>
          </label>
          <p className="mt-1 text-xs text-gray-500">
            {t('admin.countries.form.active_help', 'Inactive countries won\'t appear in the wizard')}
          </p>
        </div>

        {/* Form Actions */}
        <div className="flex justify-end space-x-3 pt-6 border-t border-gray-700">
          <button
            type="button"
            onClick={onCancel}
            disabled={loading}
            className="px-4 py-2 border border-gray-600 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {t('common.cancel', 'Cancel')}
          </button>
          <button
            type="submit"
            disabled={loading}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed flex items-center space-x-2"
          >
            {loading && (
              <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
              </svg>
            )}
            <span>{country ? t('common.save', 'Save Changes') : t('common.create', 'Create Country')}</span>
          </button>
        </div>

        {/* Submit Error */}
        {errors.submit && (
          <div className="bg-red-900/20 border border-red-800 text-red-400 px-4 py-3 rounded-lg">
            {errors.submit}
          </div>
        )}
      </form>
    </div>
  );
};

export default CountryForm;